import React from "react";
import { Sparkles } from "lucide-react";

/**
 * PageHero — the unified hero block used across every workspace page.
 *
 * Matches the visual language of the Welcome / Course / Launch Blueprint
 * pages so the platform feels like one product: eyebrow chip in amber,
 * heading in DM Sans display with a gradient accent span, and a muted
 * subtitle. Trailing slot (`right`) is reserved for primary CTAs / stats.
 *
 * Props:
 *   eyebrow  — small uppercase label above the title
 *   title    — plain string, or a React node when you need a gradient span
 *   subtitle — muted body copy under the title
 *   icon     — optional Lucide icon component for the eyebrow chip
 *   right    — right-aligned slot (CTA button, download link, stat card…)
 *   accent   — 'amber' | 'primary' (default 'amber')
 *   testid   — root data-testid, prefixed to child slots
 */
export default function PageHero({
  eyebrow,
  title,
  subtitle,
  icon: Icon = Sparkles,
  right = null,
  accent = "amber",
  className = "",
  testid = "page-hero",
  children,
}) {
  const chipCls = accent === "primary" ? "chip" : "chip-amber";
  return (
    <header
      className={`mb-10 flex flex-col md:flex-row md:items-start md:justify-between gap-6 ${className}`}
      data-testid={testid}
    >
      <div className="min-w-0">
        {/* Eyebrow chip removed per agentic edit — pages no longer show
            the small uppercase category label above the title. Callers
            can still pass `eyebrow` (ignored here) without breaking. */}
        <h1
          className="font-display text-4xl md:text-5xl leading-[1.05] text-foreground"
          data-testid={`${testid}-title`}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            className="mt-3 text-muted-foreground max-w-2xl leading-relaxed"
            data-testid={`${testid}-subtitle`}
          >
            {subtitle}
          </p>
        )}
        {children}
      </div>
      {right && (
        <div className="shrink-0 flex flex-col items-start md:items-end gap-3" data-testid={`${testid}-right`}>
          {right}
        </div>
      )}
    </header>
  );
}
